import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, state } from 'lit/decorators.js';
import a11yStyles from '../../base/outline-element/outline-element.a11y.css.lit';
import componentStyles from './idb-header.css.lit';
import '../idb-logo/idb-logo';
import '../idb-utility-menu/idb-utility-menu';
import '../../base/outline-link/outline-link';
import '../idb-language-switcher/idb-language-switcher';
import '../idb-primary-menu/idb-primary-menu';
import '../../base/outline-container/outline-container';
import { SlotController } from '../../controllers/slot-controller';
import { MobileController } from '../../controllers/mobile-controller';

/**
 * The IdbHeader component
 * @element idb-header
 * @extends OutlineElement
 * @slot language - Location for language switcher
 * @slot utility - Utility navigation menu
 * @slot logo - Slot for the log
 * @slot primary - Primary navigation menu
 * @slot search - Search feature link/form
 * @slot mobile-open - Icon slot for opening mobile menu
 * @slot mobile-close - Icon slot for closing mobile menu
 * @todo update with template partials everywhere
 * @see https://lit.dev/docs/components/events
 */
@customElement('idb-header')
export class IdbHeader extends OutlineElement {
  static styles: CSSResultGroup = [a11yStyles, componentStyles];
  private mobileController = new MobileController(this, 'lg');
  slots = new SlotController(this, false);

  /**
   * Clearly these should all be false by default.
   * Otherwise, shenanigans ensue.
   */
  @state() menuVisible: boolean;
  @state() menuOpenIconVisible: boolean;
  @state() menuCloseIconVisible: boolean;
  // Both isMobile and wasMobile are set to the same value when initialized.
  @state() isMobile = this.mobileController.isMobile;

  firstUpdated(): void {
    this.initMenu();
  }

  updated(): void {
    this.updateMenu();
  }

  render(): TemplateResult {
    return html`
      <section class="header">
        <div class="user-navigation">
          <outline-container>
            <div class="language"><slot name="language"></slot></div>
            <div class="utility"><slot name="utility"></slot></div>
          </outline-container>
        </div>
        <outline-container>
          <div class="primary-navigation">
            ${this.mobileOpenIconTemplate()}
            ${this.mobileClosedIconTemplate()}
            <div class="logo"><slot name="logo"></slot></div>
            ${this.isMobile ? null : this.primaryMenuTemplate()}
            <div class="search"><slot name="search"></slot></div>
            ${this.isMobile ? this.primaryMenuTemplate() : null}
          </div>
        </outline-container>
      </section>
    `;
  }

  /**
   * Template partial for the mobile icon that opens the menu.
   * @returns TemplateResult | null
   */
  primaryMenuTemplate(): TemplateResult | null {
    return html`
      <div 
        class="primary" 
        aria-hidden="${!this.menuVisible}"
      >
        <slot name="primary"></slot>
      </div>
    `;
  }

  /**
   * Template partial for the mobile icon that opens the menu.
   * @returns TemplateResult | null
   */
  mobileOpenIconTemplate(): TemplateResult | null {
    if (!this.isMobile) return null;

    return html`
    <div 
      class="mobile-menu-open" 
      aria-hidden="${!this.menuOpenIconVisible}" 
      @click="${this.showMenu}">
      <slot name="mobile-open"></slot>
    </div>
    `;
  }

  /**
   * Template partial for the mobile icon that closed the menu.
   * @returns TemplateResult | null
   */
  mobileClosedIconTemplate(): TemplateResult | null {
    if (!this.isMobile) return null;

    return html`
    <div 
      class="mobile-menu-close" 
      aria-hidden="${!this.menuCloseIconVisible}" 
      @click="${this.hideMenu}">
      <slot name="mobile-close"></slot>
    </div>
    `;
  }

  /**
   * Sets up the menu for the first time.
   * @returns void
   */
  initMenu(): void {
    // Set the appropriate values for our state variables.
    this.menuVisible = !this.isMobile;
    this.menuOpenIconVisible = this.isMobile && !this.menuVisible;
    this.menuCloseIconVisible = this.isMobile && this.menuVisible;
  }

  /**
   * Checks the menu, and updates as necessary.
   * @returns void
   */
  updateMenu(): void {
    // If we are still on the same breakpoint, the update
    // cycle is unnecessary.
    if (this.mobileController.isMobile === this.isMobile) {
      return;
    }

    // Update this.isMobile if the breakpoint has changed.
    if (this.mobileController.isMobile !== this.isMobile) {
      // Reset the value.
      this.isMobile = this.mobileController.isMobile;

      // If this is no longer mobile, we need to show the menu
      // no matter what and hide the open/close icons.
      if (!this.isMobile) {
        this.menuVisible = true;
        this.menuOpenIconVisible = false;
        this.menuCloseIconVisible = false;
      }

      // We are switching back to mobile, so we need to hide the menu
      // and show the open/close icons.
      if (this.isMobile) {
        this.menuVisible = false;
        this.menuOpenIconVisible = true;
        this.menuCloseIconVisible = false;
      }
    }
  }

  /**
   * Show the menu.
   * @param event Event
   * @todo: This event could be simplified a fair bit.
   */
  showMenu(event: Event): void {
    event.preventDefault();
    this.menuVisible = true;
    this.menuOpenIconVisible = false;
    this.menuCloseIconVisible = true;
  }

  /**
   * Hide the menu.
   * @param event Event
   * @todo: This event could be simplified a fair bit.
   */
  hideMenu(event: Event): void {
    event.preventDefault();
    this.menuVisible = false;
    this.menuOpenIconVisible = true;
    this.menuCloseIconVisible = false;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'idb-header': IdbHeader;
  }
}
